import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { SchemaFormRendererProps, SchemaFormField, SchemaFormUIConfig } from './types';
import { evaluateFieldConditions } from './utils/conditionEvaluator';
import * as DefaultComponents from './components/DefaultComponents';

export const SchemaFormRenderer: React.FC<SchemaFormRendererProps> = ({ 
  config, 
  onSubmit, 
  isSubmitting = false,
  className = '',
  initialData = {},
  components = {}
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const schemaPropertiesRef = useRef<string>('');

  // Merge default components with custom components
  const {
    Button = DefaultComponents.Button,
    Input = DefaultComponents.Input,
    Textarea = DefaultComponents.Textarea,
    Label = DefaultComponents.Label,
    Select = DefaultComponents.Select,
    Checkbox = DefaultComponents.Checkbox,
    RadioGroup = DefaultComponents.RadioGroup,
    Switch = DefaultComponents.Switch,
    TableInput = DefaultComponents.TableInput,
  } = components;

  // Stabilize schema properties using serialization to prevent unnecessary re-renders
  const schemaPropertiesKey = useMemo(() => 
    JSON.stringify(config.schema.properties), [config.schema.properties]);
  
  const initialDataKey = useMemo(() => 
    JSON.stringify(initialData), [initialData]);

  // Initialize form data with default values and initial data
  useEffect(() => {
    // Only re-initialize if schema properties or initial data actually changed
    if (schemaPropertiesRef.current === schemaPropertiesKey + initialDataKey && isInitialized) {
      return;
    }

    const combinedData: Record<string, any> = {};
    
    // First, apply schema defaults for untouched fields only
    Object.entries(config.schema.properties).forEach(([fieldId, field]) => {
      if (field.default !== undefined && (!isInitialized || !touchedFields.has(fieldId))) {
        combinedData[fieldId] = field.default;
      }
    });
    
    // Then, apply initial data (this takes precedence over defaults) for untouched fields only
    Object.entries(initialData).forEach(([fieldId, value]) => {
      if (config.schema.properties[fieldId] && value !== undefined && (!isInitialized || !touchedFields.has(fieldId))) {
        combinedData[fieldId] = value;
      }
    });
    
    // Only update form data if we have new data or this is the first initialization
    if (!isInitialized || Object.keys(combinedData).length > 0) {
      setFormData(prev => ({ ...prev, ...combinedData }));
      schemaPropertiesRef.current = schemaPropertiesKey + initialDataKey;
      setIsInitialized(true);
    }
  }, [schemaPropertiesKey, initialDataKey, config.schema.properties, initialData, touchedFields, isInitialized]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Only submit data for visible fields
    const visibleData: Record<string, any> = {};
    Object.entries(config.schema.properties).forEach(([fieldId, field]) => {
      if (evaluateFieldConditions(field.conditions, field.showWhen, formData)) {
        visibleData[fieldId] = formData[fieldId];
      }
    });
    
    onSubmit(visibleData);
  };

  const updateFormData = useCallback((fieldId: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    setTouchedFields(prev => new Set(prev).add(fieldId));
  }, []);

  const renderField = useCallback((fieldId: string, field: SchemaFormField) => {
    // Check if field should be visible based on conditions
    if (!evaluateFieldConditions(field.conditions, field.showWhen, formData)) {
      return null;
    }

    const value = formData[fieldId] || '';
    const uiConfig = config.uiSchema[fieldId] as SchemaFormUIConfig || {};
    const isRequired = config.schema.required?.includes(fieldId) || false;

    const renderInput = () => {
      switch (field.type) {
        case 'table':
          const tableValue = Array.isArray(value) ? value : [];
          return (
            <TableInput
              columns={field.columns || []}
              value={tableValue}
              onChange={(val: Record<string, any>[]) => updateFormData(fieldId, val)}
              minRows={field.minRows}
              maxRows={field.maxRows}
            />
          );

        case 'boolean':
          if (field.format === 'switch') {
            return (
              <div className="flex items-center space-x-2">
                <Switch
                  id={fieldId}
                  checked={value === true}
                  onCheckedChange={(checked) => updateFormData(fieldId, checked)}
                />
                <Label htmlFor={fieldId}>{field.title}</Label>
              </div>
            );
          } else {
            return (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={fieldId}
                  checked={value === true}
                  onCheckedChange={(checked) => updateFormData(fieldId, checked)}
                />
                <Label htmlFor={fieldId}>{field.title}</Label>
              </div>
            );
          }

        case 'string':
          switch (field.format) {
            case 'textarea':
              return (
                <Textarea
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateFormData(fieldId, e.target.value)}
                  placeholder={uiConfig['ui:placeholder']}
                  rows={uiConfig['ui:options']?.rows || 4}
                />
              );

            case 'select':
              return (
                <Select
                  value={value}
                  onValueChange={(val) => updateFormData(fieldId, val)}
                  placeholder={uiConfig['ui:placeholder'] || `Select ${field.title?.toLowerCase()}`}
                >
                  {(field.enum || []).map((option, index) => (
                    <option key={index} value={String(option)}>
                      {field.enumNames?.[index] || String(option)}
                    </option>
                  ))}
                </Select>
              );

            case 'radio':
              return (
                <RadioGroup
                  value={value}
                  onValueChange={(val) => updateFormData(fieldId, val)}
                >
                  {(field.enum || []).map((option, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <DefaultComponents.RadioGroupItem 
                        value={String(option)} 
                        id={`${fieldId}-${index}`} 
                      />
                      <Label htmlFor={`${fieldId}-${index}`}>
                        {field.enumNames?.[index] || String(option)}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              );

            case 'checkboxes':
            case 'multiselect':
              const selectedValues = Array.isArray(value) ? value : [];
              return (
                <div className="space-y-2">
                  {(field.enum || []).map((option, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${fieldId}-${index}`}
                        checked={selectedValues.includes(String(option))}
                        onCheckedChange={(checked) => {
                          const newValues = checked
                            ? [...selectedValues, String(option)]
                            : selectedValues.filter(v => v !== String(option));
                          updateFormData(fieldId, newValues);
                        }}
                      />
                      <Label htmlFor={`${fieldId}-${index}`}>
                        {field.enumNames?.[index] || String(option)}
                      </Label>
                    </div>
                  ))}
                </div>
              );

            case 'file':
              return (
                <Input
                  type="file"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];
                    updateFormData(fieldId, file);
                  }}
                  accept={uiConfig['ui:options']?.accept}
                />
              );

            default:
              return (
                <Input
                  type={field.format === 'email' ? 'email' : field.format === 'date' ? 'date' : field.format === 'date-time' ? 'datetime-local' : 'text'}
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormData(fieldId, e.target.value)}
                  placeholder={uiConfig['ui:placeholder']}
                  minLength={field.minLength}
                  maxLength={field.maxLength}
                  pattern={field.pattern}
                />
              );
          }

        case 'number':
        case 'integer':
          return (
            <Input
              type="number"
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormData(fieldId, field.type === 'integer' ? parseInt(e.target.value) || 0 : parseFloat(e.target.value) || 0)}
              placeholder={uiConfig['ui:placeholder']}
              min={field.minimum}
              max={field.maximum}
              step={field.type === 'integer' ? 1 : 'any'}
            />
          );

        default:
          return (
            <Input
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormData(fieldId, e.target.value)}
              placeholder={uiConfig['ui:placeholder']}
            />
          );
      }
    };

    return (
      <div key={fieldId} className="space-y-2">
        {field.type !== 'boolean' && (
          <Label htmlFor={fieldId}>
            {field.title || fieldId}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </Label>
        )}
        {renderInput()}
        {field.description && (
          <p className="text-sm text-gray-600">{field.description}</p>
        )}
        {uiConfig['ui:help'] && (
          <p className="text-sm text-gray-600">{uiConfig['ui:help']}</p>
        )}
      </div>
    );
  }, [formData, config.uiSchema, config.schema.required, updateFormData]);

  // Get ordered field IDs, falling back to object keys if no order is specified
  const orderedFieldIds = config.fieldOrder || Object.keys(config.schema.properties);
  const filteredFieldIds = orderedFieldIds.filter(fieldId => config.schema.properties[fieldId]);

  return (
    <div className={`space-y-6 ${className}`}>
      <div>
        <h2 className="text-2xl font-bold">{config.title}</h2>
        {config.description && (
          <p className="text-gray-600 mt-2">{config.description}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {filteredFieldIds.map((fieldId) =>
          renderField(fieldId, config.schema.properties[fieldId])
        )}

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : config.settings.submitButtonText}
        </Button>
      </form>
    </div>
  );
};
